import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { getKdsStations, saveKdsStation, getCategoryListing } from "../../utils/apiCalls";
import { errorToast } from "../../shared/Toast";
import { C, tbl } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";

/**
 * LAN kitchen displays, grouped by branch.
 *
 * WHY THIS IS MOSTLY READ-ONLY
 * A kitchen's address lives on the shop's own network and this panel cannot reach it — there is no
 * way from here to tell a correct IP from a typo. The POS can: it sits on that network, already holds
 * a live socket to each display, and is where the list is authored. So the POS pushes the list up and
 * this page shows what arrived, so head office can see how a branch is wired without guessing.
 *
 * The Add button is for the other case: a site running self-order screens with no POS to sync from.
 * Those rows are marked `admin`, and a POS sync will never touch them.
 */

// What a self-order screen puts on an order. A kiosk order is a Pickup order — the same value the
// pickup app uses — so restricting a station to anything else means it takes no screen tickets.
const SCREEN_ORDER_TYPE = "Pickup";
const ORDER_TYPES = ["Pickup", "Delivery", "Drivethrough", "Dinein", "Curbside"];

const EMPTY_FORM = {
  branchId: "",
  name: "",
  ip: "",
  port: 3399,
  categoryIds: [],
  orderTypeRefs: [],
  kdsForAggregator: false,
  active: true,
};

const KdsStationSection = ({ selectedMerchant, branches }) => {
  const [stations, setStations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState(null); // a station, or the string 'new'
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchStations = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") {
      setStations([]);
      return;
    }
    const data = await getKdsStations(selectedMerchant);
    setStations(data || []);
  }, [selectedMerchant]);

  useEffect(() => {
    setIsLoading(true);
    fetchStations().finally(() => setIsLoading(false));
  }, [fetchStations]);

  // Only the Add/Edit form needs these, so they are not fetched until it opens.
  useEffect(() => {
    if (!editing || categories.length || !selectedMerchant || selectedMerchant === "all") return;
    // Returns the array on success but `{ data: [] }` on failure, so guard rather than trust it —
    // same check coupons.js makes on the identical call.
    getCategoryListing(selectedMerchant).then((list) => setCategories(Array.isArray(list) ? list : []));
  }, [editing, categories.length, selectedMerchant]);

  const byBranch = useMemo(() => {
    const groups = new Map();
    stations.forEach((s) => {
      if (!groups.has(s.branchId)) {
        groups.set(s.branchId, { branchName: s.branchName || `Branch #${s.branchId}`, rows: [] });
      }
      groups.get(s.branchId).rows.push(s);
    });
    return [...groups.entries()].sort((a, b) =>
      String(a[1].branchName).localeCompare(String(b[1].branchName))
    );
  }, [stations]);

  const openNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditing("new");
  };

  const openEdit = (station) => {
    setForm({
      branchId: station.branchId,
      name: station.name || "",
      ip: station.ip || "",
      port: station.port || 3399,
      categoryIds: (station.categories || []).map((c) => c.id),
      orderTypeRefs: station.orderTypeRefs || [],
      kdsForAggregator: !!station.kdsForAggregator,
      active: station.active !== false,
    });
    setEditing(station);
  };

  const handleSave = async () => {
    if (!form.branchId) {
      errorToast("Select a branch");
      return;
    }
    if (!form.name.trim()) {
      errorToast("Give the station a name");
      return;
    }
    setSaving(true);
    const result = await saveKdsStation(editing === "new" ? null : editing.id, {
      ...form,
      branchId: Number(form.branchId),
      port: Number(form.port) || 3399,
      // Rows added here route by category — product-level routing is set on the POS.
      byCategory: true,
    });
    setSaving(false);
    if (result) {
      setEditing(null);
      fetchStations();
    }
  };

  // What this station cooks, in one line — the full lists are long and the table is a scan surface.
  const describeRouting = (s) => {
    if (s.locationBased) return "By location";
    if (s.byCategory) {
      // The POS's "All" option, which arrives as a flag rather than a category. Without this a
      // station that cooks everything would read as "Nothing selected" — the exact opposite.
      if (s.allCategories) return "All categories";
      const names = (s.categories || []).map((c) => c.name).filter(Boolean);
      if (!names.length) return "Nothing selected";
      return names.length > 3 ? `${names.slice(0, 3).join(", ")} +${names.length - 3}` : names.join(", ");
    }
    const n = s.products?.length || 0;
    return n ? `${n} product${n === 1 ? "" : "s"}` : "Nothing selected";
  };

  const relative = (value) => {
    if (!value) return "Never";
    const mins = Math.round((Date.now() - new Date(value).getTime()) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return new Date(value).toLocaleDateString();
  };

  // A POS-owned row would be overwritten by the tablet's next sync, so it is shown but not editable.
  const editable = editing && editing !== "new" ? editing.source === "admin" : true;

  // Restricted to types that never include what a screen sends: the station is invisible to kiosks.
  const takesNoScreenOrders =
    form.orderTypeRefs.length > 0 && !form.orderTypeRefs.includes(SCREEN_ORDER_TYPE);

  return (
    <Fragment>
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "28px 0 14px" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>LAN kitchen stations</div>
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
            Kitchen displays on the branch network. Self-order screens send tickets straight to these.
            The POS owns the list — edit it there, then press Save to re-sync.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <Button
          size="sm"
          disabled={!selectedMerchant || selectedMerchant === "all"}
          style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: 8,
            color: C.textMed, fontWeight: 600, fontSize: 12, padding: "8px 16px",
          }}
          onClick={openNew}
        >
          + Add manually
        </Button>
      </div>

      <RCard style={{ padding: 0, overflow: "hidden" }}>
        {isLoading ? (
          <Loader />
        ) : !stations.length ? (
          <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>
            {!selectedMerchant || selectedMerchant === "all"
              ? "Select a merchant"
              : "No kitchen stations yet — open Settings → Kitchen IP Address on the POS and press Save."}
          </p>
        ) : (
          byBranch.map(([branchId, group]) => (
            <div key={branchId}>
              <div
                style={{
                  padding: "10px 16px", background: C.bg,
                  borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
                  fontWeight: 700, fontSize: 13, color: C.textMed,
                }}
              >
                {group.branchName}
                <span style={{ fontWeight: 500, color: C.textLight, marginLeft: 8 }}>
                  {group.rows.length} station{group.rows.length === 1 ? "" : "s"}
                </span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      {["Station", "Address", "Routes", "Order types", "Source", "Status", "Last seen", "Actions"].map((h) => (
                        <th key={h} style={{ ...tbl.th, ...(h === "Actions" && { textAlign: "right" }) }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((s) => (
                      <tr key={s.id} className="rpt-row">
                        <td style={tbl.td}>
                          <div style={{ fontWeight: 600, color: C.text }}>{s.name}</div>
                          {s.kdsForAggregator && (
                            <span style={{ fontSize: 11, color: C.textLight }}>Aggregator orders only</span>
                          )}
                        </td>
                        <td style={tbl.td}>
                          <code style={{ fontSize: 12, color: C.textMed }}>
                            {s.ip}{s.port && s.port !== 3399 ? `:${s.port}` : ""}
                          </code>
                        </td>
                        <td style={{ ...tbl.td, maxWidth: 260 }}>
                          <span style={{ fontSize: 12, color: C.textMed }}>{describeRouting(s)}</span>
                        </td>
                        <td style={tbl.td}>
                          <span style={{ fontSize: 12, color: C.textLight }}>
                            {s.orderTypeRefs?.length ? s.orderTypeRefs.join(", ") : "All"}
                          </span>
                        </td>
                        <td style={tbl.td}>
                          <Badge
                            color={s.source === "pos" ? C.textMed : C.coral}
                            bg={s.source === "pos" ? C.bg : C.coralLight}
                          >
                            {s.source === "pos" ? "From POS" : "Manual"}
                          </Badge>
                        </td>
                        <td style={tbl.td}>
                          <Badge
                            color={s.active ? C.green : C.textLight}
                            bg={s.active ? C.greenLight : C.bg}
                          >
                            {s.active ? "Active" : "Off"}
                          </Badge>
                        </td>
                        <td style={tbl.td}>
                          <span style={{ fontSize: 12, color: C.textLight }}>{relative(s.lastSeenAt)}</span>
                        </td>
                        <td style={{ ...tbl.td, textAlign: "right" }}>
                          {s.source === "admin" ? (
                            <Button
                              size="sm"
                              style={{
                                background: C.white, border: `1px solid ${C.border}`,
                                borderRadius: 6, color: C.textMed, fontSize: 11, fontWeight: 700,
                              }}
                              onClick={() => openEdit(s)}
                            >
                              Edit
                            </Button>
                          ) : (
                            <span style={{ fontSize: 11, color: C.textLight }}>Edit on the POS</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </RCard>

      <Modal isOpen={!!editing} toggle={() => setEditing(null)}>
        <ModalHeader toggle={() => setEditing(null)}>
          {editing === "new" ? "Add kitchen station" : `Edit ${editing?.name || "station"}`}
        </ModalHeader>
        <ModalBody>
          {!editable && (
            <div style={{ fontSize: 12, color: C.orange, marginBottom: 12 }}>
              This station came from the POS. Changes here would be overwritten on its next sync —
              edit it on the POS instead.
            </div>
          )}

          <Label>Branch *</Label>
          <Input
            type="select"
            value={form.branchId}
            disabled={editing !== "new"}
            onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value }))}
            style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}
          >
            <option value="">Select a branch</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </Input>

          <Label>Name *</Label>
          <Input
            type="text"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Grill"
            style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}
          />

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 2 }}>
              <Label>IP address *</Label>
              <Input
                type="text"
                value={form.ip}
                onChange={(e) => setForm((p) => ({ ...p, ip: e.target.value }))}
                placeholder="192.168.1.42"
                style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 4 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Label>Port</Label>
              <Input
                type="number"
                value={form.port}
                onChange={(e) => setForm((p) => ({ ...p, port: e.target.value }))}
                style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 4 }}
              />
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.textLight, marginBottom: 12 }}>
            This address is on the shop's network, so it cannot be checked from here. The POS shows
            each display's live connection status.
          </div>

          <Label>Order types</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
            {ORDER_TYPES.map((ot) => {
              const on = form.orderTypeRefs.includes(ot);
              return (
                <button
                  key={ot}
                  type="button"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      orderTypeRefs: on
                        ? p.orderTypeRefs.filter((x) => x !== ot)
                        : [...p.orderTypeRefs, ot],
                    }))
                  }
                  style={{
                    padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                    cursor: "pointer",
                    border: `1px solid ${on ? C.coral : C.border}`,
                    background: on ? C.coralLight : C.white,
                    color: on ? C.coral : C.textMed,
                  }}
                >
                  {ot}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: takesNoScreenOrders ? C.orange : C.textLight, marginBottom: 12 }}>
            {takesNoScreenOrders
              ? `Self-order screens place ${SCREEN_ORDER_TYPE} orders — with ${SCREEN_ORDER_TYPE} unselected, this station will receive none of them.`
              : "None selected means this station takes every order type."}
          </div>

          <Label>Categories</Label>
          <Input
            type="select"
            multiple
            value={form.categoryIds.map(String)}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                categoryIds: [...e.target.selectedOptions].map((o) => Number(o.value)),
              }))
            }
            style={{ borderRadius: 8, border: `1px solid ${C.border}`, height: 140, marginBottom: 4 }}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Input>
          <div style={{ fontSize: 12, color: C.textLight, marginBottom: 12 }}>
            Product-level routing is set on the POS — stations added here route by category.
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.kdsForAggregator}
              onChange={(e) => setForm((p) => ({ ...p, kdsForAggregator: e.target.checked }))}
              style={{ width: 16, height: 16, accentColor: C.coral }}
            />
            Aggregator orders only
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer", marginTop: 8 }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
              style={{ width: 16, height: 16, accentColor: C.coral }}
            />
            Active
          </label>
        </ModalBody>
        <ModalFooter>
          <Button
            style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed }}
            onClick={() => setEditing(null)}
          >
            Cancel
          </Button>
          <Button
            disabled={saving || !editable}
            style={{
              background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
              border: "none", borderRadius: 8, fontWeight: 600,
            }}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default KdsStationSection;
